import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { downloadS3Document, generateDocMagicPackage, getDocMagicHistory, getDocMagicPackages } from 'services'
import { Button, Input2, Modal, Select2, Tooltip } from 'stories/components'
import { formatTime } from 'utils'

type DocMagicPackageError = {
  severity: string
  message: string
  code?: string
  category?: string
}

type DocMagicHistoryItem = {
  id: number
  packageType: string
  planCode?: string
  status: string
  requestXmlKey?: string
  responseXmlKey?: string
  files?: { name: string; fileKey: string }[]
  message?: string
  errors?: DocMagicPackageError[]
  createdBy?: string
  createdAt: string
}

type DocMagicPackageOption = {
  type: string
  label: string
  documentGroupType: string
}

type DocMagicLoanInfo = {
  rate: number | null
  hasRate: boolean
  loanAmount: number | null
  loanTerm: number | null
  firstPaymentDate: string | null
  amortizationType: string
  missingLoanEstimateContacts?: string[]
}

const FALLBACK_PACKAGES: DocMagicPackageOption[] = [
  { type: 'Predisclosure', label: 'Initial Disclosures', documentGroupType: 'Predisclosure' },
  { type: 'Redisclosure', label: 'Redisclosures', documentGroupType: 'Redisclosure' },
  { type: 'RateLock', label: 'Rate Lock', documentGroupType: 'Predisclosure' },
  { type: 'ClosingDisclosure', label: 'Closing Disclosure', documentGroupType: 'Closing' },
  { type: 'PreClosing', label: 'Pre-Closing', documentGroupType: 'Closing' },
  { type: 'ClosingDocuments', label: 'Closing Documents', documentGroupType: 'Closing' },
]

const packageErrors = (item: DocMagicHistoryItem): DocMagicPackageError[] => {
  if (item.errors?.length) return item.errors
  if (!item.message) return []
  return item.message
    .split('\n')
    .map((message) => message.trim())
    .filter(Boolean)
    .map((message) => ({ severity: 'Error', message }))
}

const packageLabel = (type: string, packages: DocMagicPackageOption[]) =>
  packages.find((item) => item.type === type)?.label || type

export default function DocMagicClosingScreen() {
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<DocMagicHistoryItem[]>([])
  const [packages, setPackages] = useState<DocMagicPackageOption[]>([])
  const [planCodes, setPlanCodes] = useState<string[]>([])
  const [loan, setLoan] = useState<DocMagicLoanInfo | null>(null)
  const [selectedType, setSelectedType] = useState('Predisclosure')
  const [planCode, setPlanCode] = useState('')
  const [noteRate, setNoteRate] = useState('')
  const [detailItem, setDetailItem] = useState<DocMagicHistoryItem | null>(null)

  const selectedPackage = useMemo(
    () => packages.find((item) => item.type === selectedType) || packages[0],
    [packages, selectedType],
  )

  const loadPage = async () => {
    setLoading(true)
    try {
      const [historyResult, configResult] = await Promise.allSettled([getDocMagicHistory(), getDocMagicPackages()])
      const records = historyResult.status === 'fulfilled' ? historyResult.value : []
      const config = configResult.status === 'fulfilled' ? configResult.value : null
      setHistory(Array.isArray(records) ? records : [])
      const nextPackages: DocMagicPackageOption[] =
        Array.isArray(config?.packages) && config.packages.length ? config.packages : FALLBACK_PACKAGES
      const nextPlanCodes: string[] = Array.isArray(config?.planCodes)
        ? config.planCodes.map((code: string) => String(code || '').trim()).filter(Boolean)
        : []
      setPackages(nextPackages)
      setPlanCodes(nextPlanCodes)
      setLoan(config?.loan || null)
      setSelectedType(
        (current) => nextPackages.find((item) => item.type === current)?.type || nextPackages[0]?.type || current,
      )
      setPlanCode((current) => {
        if (current && nextPlanCodes.includes(current)) return current
        return nextPlanCodes[0] || ''
      })
      if (config?.loan?.hasRate && config.loan.rate) setNoteRate(String(config.loan.rate))
      if (historyResult.status === 'rejected' || configResult.status === 'rejected') {
        toast('Could not load all DocMagic data. Some information may be missing.', { type: 'warning' })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPage()
  }, [])

  const onSelectPackage = (type: string) => {
    setSelectedType(type)
  }

  const onGenerate = async () => {
    if (!planCode.trim()) {
      toast('A DocMagic Plan Code is required. Add codes on Admin → Vendors → DocMagic, then select one here.', {
        type: 'error',
      })
      return
    }
    const rate = Number(noteRate)
    if (!(rate > 0) && !loan?.hasRate) {
      toast('An interest rate is required. Lock the loan or enter the quoted rate.', { type: 'error' })
      return
    }
    if (loan?.missingLoanEstimateContacts?.length) {
      toast(
        `Loan Estimate Page 3 Lender and Broker must be complete. Missing: ${loan.missingLoanEstimateContacts.join(
          ', ',
        )}.`,
        { type: 'error' },
      )
      return
    }
    setLoading(true)
    try {
      const record = await generateDocMagicPackage({
        packageType: selectedType,
        planCode: planCode.trim(),
        noteRate: rate > 0 ? rate : undefined,
      })
      if (record?.status === 'Failed') {
        const errors = packageErrors(record)
        toast(
          errors[0]?.message ||
            record.message ||
            'DocMagic audit failed. Open Package Details for the full status message list.',
          { type: 'error' },
        )
      } else {
        toast(`${selectedPackage?.label || 'Package'} generated`, { type: 'success' })
      }
      await loadPage()
    } catch (e: any) {
      toast(e?.message || 'DocMagic generation failed', { type: 'error' })
      await loadPage()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <LayoutLoading show={loading} />
      <h2 className="text-2xl font-bold flex items-center mb-3">DocMagic</h2>
      <p className="text-sm text-gray-600 mb-4">
        Generate runs a DocMagic Audit first. Documents are created only if Audit passes. Each package uses the same
        loan XML with a different DocMagic document group. Choose a plan code from the list configured on Admin →
        Vendors → DocMagic.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {packages.map((item) => (
          <button
            key={item.type}
            type="button"
            disabled={loading}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              selectedType === item.type
                ? 'bg-shade-blue text-white border-shade-blue'
                : 'border-slate-300 text-slate-700 hover:border-shade-blue'
            }`}
            onClick={() => onSelectPackage(item.type)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2 mb-4">
        <Select2
          id="planCode"
          name="planCode"
          title="Plan Code"
          required
          hasDefaultOption
          defaultOptionText={planCodes.length ? '- Select -' : 'Add plan codes in Vendors'}
          tooltip="Codes from Admin → Vendors → DocMagic"
          options={planCodes}
          value={planCode}
          onChange={(value) => setPlanCode(value)}
        />
        <Input2
          name="noteRate"
          title="Interest Rate"
          type="number"
          required
          value={noteRate}
          readOnly={!!loan?.hasRate}
          onChange={(value) => setNoteRate(value)}
        />
      </div>
      <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 mb-4">
        <p className="font-semibold text-slate-700 mb-1">Required for {selectedPackage?.label || 'this package'}</p>
        <ul className="list-disc ml-4 space-y-0.5">
          <li>Plan Code from Admin → Vendors → DocMagic</li>
          <li>
            Interest Rate
            {loan?.hasRate ? ` (from loan: ${loan.rate})` : ' — this loan is unlocked; enter the quoted rate'}
          </li>
          <li>
            Loan Amount{loan?.loanAmount ? ` ($${Number(loan.loanAmount).toLocaleString()})` : ' — missing'}
            {loan?.loanTerm ? `, term ${loan.loanTerm} months` : ''}
          </li>
          <li>
            First payment date
            {loan?.firstPaymentDate
              ? ` (${loan.firstPaymentDate})`
              : ' — set First Payment Date on Document Generate, or set the closing date'}
          </li>
          <li>
            Loan Estimate Page 3 Lender and Broker — Company, NMLS/Lic. ID, Contact, Contact NMLS/Lic. ID, Email, and
            Phone
            {loan?.missingLoanEstimateContacts?.length
              ? ` — missing ${loan.missingLoanEstimateContacts.join(', ')}`
              : loan
              ? ' (complete)'
              : ''}
          </li>
          <li>Generate audits with DocMagic first; Process (PDFs) runs only when Audit has no Fatal errors</li>
        </ul>
      </div>
      <div className="text-right mb-4">
        <Button loading={loading} onClick={onGenerate}>
          {`Generate ${selectedPackage?.label || 'Package'}`}
        </Button>
      </div>
      <table className="w-full text-sm text-left text-gray-900">
        <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-3 py-3">No</th>
            <th className="px-3 py-3">Package</th>
            <th className="px-3 py-3">Created By</th>
            <th className="px-3 py-3">Created At</th>
            <th className="px-3 py-3">Status</th>
            <th className="px-3 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {history.map((item, index) => (
            <tr key={item.id} className="border-b">
              <td className="px-3 py-3">{index + 1}</td>
              <td className="px-3 py-3">{packageLabel(item.packageType, packages)}</td>
              <td className="px-3 py-3">{item.createdBy}</td>
              <td className="px-3 py-3">{formatTime(item.createdAt)}</td>
              <td className="px-3 py-3">
                <span
                  className={`p-1 min-w-[100px] inline-block text-center rounded-md ${
                    item.status === 'Completed'
                      ? 'bg-green-300'
                      : item.status === 'Failed'
                      ? 'bg-red-200'
                      : 'bg-gray-300'
                  }`}
                >
                  {item.status}
                </span>
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-1 text-shade-blue">
                  {item.requestXmlKey ? (
                    <Tooltip message="Request XML">
                      <button
                        aria-label="Request XML"
                        className="p-1 hover-shadow1 rounded"
                        onClick={() =>
                          downloadS3Document(item.requestXmlKey as string, `docmagic-request-${item.id}.xml`)
                        }
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  ) : null}
                  {item.responseXmlKey ? (
                    <Tooltip message="Response XML">
                      <button
                        aria-label="Response XML"
                        className="p-1 hover-shadow1 rounded"
                        onClick={() =>
                          downloadS3Document(item.responseXmlKey as string, `docmagic-response-${item.id}.xml`)
                        }
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  ) : null}
                  {item.files?.map((file) => (
                    <Tooltip key={file.fileKey} message={file.name || 'PDF'}>
                      <button
                        aria-label={file.name || 'PDF'}
                        className="p-1 hover-shadow1 rounded"
                        onClick={() => downloadS3Document(file.fileKey, file.name)}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  ))}
                  <Tooltip message="Details">
                    <button
                      aria-label="Details"
                      className="p-1 hover-shadow1 rounded"
                      onClick={() => setDetailItem(item)}
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              </td>
            </tr>
          ))}
          {history.length === 0 && (
            <tr>
              <td className="px-3 py-4 text-gray-500" colSpan={6}>
                No DocMagic packages yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {detailItem ? (
        <DocMagicPackageDetailsModal
          item={detailItem}
          label={packageLabel(detailItem.packageType, packages)}
          onClose={() => setDetailItem(null)}
        />
      ) : null}
    </div>
  )
}

const DocMagicPackageDetailsModal = ({
  item,
  label,
  onClose,
}: {
  item: DocMagicHistoryItem
  label: string
  onClose: () => void
}) => {
  const errors = packageErrors(item)

  return (
    <Modal title="DocMagic Package Details" titleOkay="" isOpen onClose={onClose}>
      <div className="w-full max-w-[720px] space-y-4">
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Package</p>
            <p className="text-gray-900">{label}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</p>
            <p className="text-gray-900">{item.status}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Plan Code</p>
            <p className="text-gray-900">{item.planCode || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Created By</p>
            <p className="text-gray-900">{item.createdBy || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Created At</p>
            <p className="text-gray-900">{formatTime(item.createdAt)}</p>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Status Messages
          </p>
          {errors.length ? (
            <div className="max-h-[360px] overflow-auto border border-slate-200 rounded">
              <table className="w-full text-sm text-left text-gray-900">
                <thead className="sticky top-0 text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 w-24">Severity</th>
                    <th className="px-3 py-2 w-24">Category</th>
                    <th className="px-3 py-2">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((error, index) => (
                    <tr key={`${error.severity}-${error.message}-${index}`} className="border-b last:border-b-0">
                      <td className="px-3 py-2 align-top whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            /fatal|fail|error/i.test(error.severity)
                              ? 'bg-red-100 text-red-800'
                              : /warn/i.test(error.severity)
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {error.severity}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top whitespace-nowrap text-slate-600">
                        {error.category || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-pre-wrap break-words">{error.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No status messages recorded for this package.</p>
          )}
        </div>
      </div>
    </Modal>
  )
}
